import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, TextField, MenuItem, Button, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert,
  Snackbar, IconButton, Checkbox,
} from '@mui/material';
import { Search, Email, Replay, PictureAsPdf, DeleteForever } from '@mui/icons-material';
import api from '../../api/client';
import CallTimesPanel from '../../components/CallTimesPanel';
import { useAuth } from '../../contexts/AuthContext';
import { formatTime24, ensureTimezone } from '../../utils/time';

interface CallRecord {
  id: number; cad_number: string; case_number: string;
  call_type_code: string; call_type_name: string;
  priority: number; status: string; location_address: string;
  location_apt?: string; cross_street?: string;
  reporting_party: string; reporting_phone?: string;
  narrative: string; agency_name: string; created_at: string;
  dispatched_at?: string; closed_at?: string; disposition_name?: string;
  disposition?: string;
}
interface CallDetail extends CallRecord {
  units: { id: number; callsign: string; status_name: string; status_color: string }[];
  audit_trail: { id: number; timestamp: string; action: string; username: string; old_value: string; new_value: string; details: string; target_name: string }[];
}
interface CallType { id: number; code: string; name: string; }

const PRIORITY_COLORS: Record<number, string> = {
  1: '#f44336', 2: '#ff9800', 3: '#2196f3', 4: '#4caf50', 5: '#9e9e9e',
};

const ACTION_COLORS: Record<string, string> = {
  call_created: '#2196f3',
  call_updated: '#ff9800',
  call_closed: '#4caf50',
  unit_dispatched: '#e91e63',
  unit_status_change: '#9c27b0',
  unit_cleared: '#4caf50',
  call_merged: '#673ab7',
  unit_logon: '#00bcd4',
  unit_logoff: '#795548',
};

const DENSE_CELL = { py: 0.25, px: 1, fontSize: 13, lineHeight: 1.3 } as const;
const DENSE_HEADER = { ...DENSE_CELL, fontWeight: 700 } as const;

export default function CallHistoryPage() {
  const { user } = useAuth();
  const isSuperadmin = !!user && (user.role === 'superadmin' || user.superadmin_access);
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Superadmin multi-select delete
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Search filters
  const [searchText, setSearchText] = useState('');
  const [filterUnit, setFilterUnit] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCallType, setFilterCallType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Call detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<CallDetail | null>(null);

  // Email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [snack, setSnack] = useState('');

  useEffect(() => { ensureTimezone(); }, []);

  useEffect(() => {
    api.get('/call-types').then((r) => setCallTypes(r.data)).catch(() => {});
  }, []);

  const doSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchText) params.q = searchText;
      if (filterUnit) params.unit = filterUnit;
      if (filterStatus) params.status = filterStatus;
      if (filterCallType) params.call_type_id = filterCallType;
      if (filterPriority) params.priority = filterPriority;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get('/calls/search', { params });
      setCalls(r.data);
      setSelected(new Set());
      setSearched(true);
    } catch { /* ignore */ }
    setLoading(false);
  }, [searchText, filterUnit, filterStatus, filterCallType, filterPriority, dateFrom, dateTo]);

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (
      prev.size === calls.length ? new Set() : new Set(calls.map((c) => c.id))
    ));
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const r = await api.post('/calls/bulk-delete', { ids: Array.from(selected) });
      const n = r.data?.deleted ?? selected.size;
      const nextSeq = r.data?.next_seq;
      setSnack(`Deleted ${n} call(s).${nextSeq ? ` Next CAD number: ${nextSeq}.` : ''}`);
      setDeleteOpen(false);
      setSelected(new Set());
      doSearch();
    } catch {
      setSnack('Failed to delete calls.');
    }
    setDeleting(false);
  };

  const openDetail = async (callId: number) => {
    try {
      const r = await api.get(`/calls/${callId}/detail`);
      setSelectedCall(r.data);
      setDetailOpen(true);
    } catch { /* ignore */ }
  };

  const clearFilters = () => {
    setSearchText('');
    setFilterUnit('');
    setFilterStatus('');
    setFilterCallType('');
    setFilterPriority('');
    setDateFrom('');
    setDateTo('');
    setCalls([]);
    setSearched(false);
  };

  const handleReopenCall = async () => {
    if (!selectedCall) return;
    try {
      await api.post(`/calls/${selectedCall.id}/reopen`);
      setSnack(`Call ${selectedCall.cad_number} reopened`);
      setDetailOpen(false);
      doSearch();
    } catch {
      setSnack('Failed to reopen call.');
    }
  };

  const handleEmail = async () => {
    if (!selectedCall || !emailTo) return;
    try {
      await api.post(`/calls/${selectedCall.id}/email`, { to_email: emailTo });
      setSnack(`Call report emailed to ${emailTo}`);
      setEmailOpen(false);
      setEmailTo('');
    } catch {
      setSnack('Failed to send email. Check SMTP configuration.');
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Call History</Typography>

      {/* Search Filters */}
      <Paper sx={{ p: 1.5, mb: 1 }}>
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 2.5 }}>
            <TextField
              fullWidth size="small" label="Search (CAD#, address, narrative, RP)"
              value={searchText} onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField
              fullWidth size="small" label="Unit"
              value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField
              fullWidth size="small" label="Status" select
              value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="dispatched">Dispatched</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField
              fullWidth size="small" label="Call Type" select
              value={filterCallType} onChange={(e) => setFilterCallType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {callTypes.map((ct) => (
                <MenuItem key={ct.id} value={String(ct.id)}>{ct.code} - {ct.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 1 }}>
            <TextField
              fullWidth size="small" label="Priority" select
              value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {[1,2,3,4,5].map((p) => <MenuItem key={p} value={String(p)}>P{p}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField
              fullWidth size="small" label="From" type="date"
              value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField
              fullWidth size="small" label="To" type="date"
              value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button variant="contained" size="small" startIcon={<Search />} onClick={doSearch} disabled={loading}>
                Search
              </Button>
              <Button variant="outlined" size="small" onClick={clearFilters}>Clear</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      {searched && calls.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>No calls found matching your criteria.</Alert>
      )}

      {isSuperadmin && calls.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Button
            variant="contained" color="error" size="small" startIcon={<DeleteForever />}
            disabled={selected.size === 0}
            onClick={() => setDeleteOpen(true)}
          >
            Delete Selected ({selected.size})
          </Button>
          <Typography variant="caption" color="text.secondary">
            Superadmin — permanently deletes selected calls; CAD numbering continues after the highest remaining call.
          </Typography>
        </Box>
      )}

      {calls.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {isSuperadmin && (
                  <TableCell padding="checkbox" sx={DENSE_HEADER}>
                    <Checkbox
                      size="small"
                      checked={calls.length > 0 && selected.size === calls.length}
                      indeterminate={selected.size > 0 && selected.size < calls.length}
                      onChange={toggleAll}
                    />
                  </TableCell>
                )}
                <TableCell sx={DENSE_HEADER}>CAD #</TableCell>
                <TableCell sx={DENSE_HEADER}>Date/Time</TableCell>
                <TableCell sx={DENSE_HEADER}>Type</TableCell>
                <TableCell sx={DENSE_HEADER}>Priority</TableCell>
                <TableCell sx={DENSE_HEADER}>Location</TableCell>
                <TableCell sx={DENSE_HEADER}>Status</TableCell>
                <TableCell sx={DENSE_HEADER}>Disposition</TableCell>
                <TableCell sx={DENSE_HEADER}>RP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((c) => (
                <TableRow key={c.id} hover selected={selected.has(c.id)}>
                  {isSuperadmin && (
                    <TableCell padding="checkbox" sx={DENSE_CELL}>
                      <Checkbox
                        size="small"
                        checked={selected.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ ...DENSE_CELL, fontFamily: 'monospace', fontWeight: 700, color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => openDetail(c.id)}
                  >
                    {c.cad_number}
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, whiteSpace: 'nowrap' }}>
                    {formatTime24(c.created_at)}
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>{c.call_type_code}</TableCell>
                  <TableCell sx={DENSE_CELL}>
                    <Chip size="small" label={`P${c.priority}`}
                      sx={{ bgcolor: PRIORITY_COLORS[c.priority] || '#9e9e9e', color: '#fff' }} />
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>{c.location_address}</TableCell>
                  <TableCell sx={DENSE_CELL}>
                    <Chip size="small" label={c.status.toUpperCase()} />
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>{c.disposition_name || c.disposition || '—'}</TableCell>
                  <TableCell sx={DENSE_CELL}>{c.reporting_party || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Call Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        {selectedCall && (
          <>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedCall.cad_number} — {selectedCall.call_type_name || selectedCall.call_type_code}
              <IconButton size="small" title="PDF Report" onClick={() => { if (selectedCall) { const t = localStorage.getItem('token'); window.open(`/api/calls/${selectedCall.id}/pdf?token=${encodeURIComponent(t || '')}`, '_blank'); } }} sx={{ ml: 'auto' }}>
                <PictureAsPdf />
              </IconButton>
              <IconButton size="small" title="Email this call" onClick={() => setEmailOpen(true)}>
                <Email />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {/* Header chips */}
              <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`P${selectedCall.priority}`}
                  sx={{ bgcolor: PRIORITY_COLORS[selectedCall.priority] || '#9e9e9e', color: '#fff' }} />
                <Chip size="small" label={selectedCall.status.toUpperCase()} />
                {(selectedCall.disposition_name || selectedCall.disposition) && (
                  <Chip size="small" label={selectedCall.disposition_name || selectedCall.disposition} color="success" />
                )}
                {selectedCall.case_number && (
                  <Chip size="small" label={`Case: ${selectedCall.case_number}`} variant="outlined" />
                )}
              </Box>

              {/* Timestamps */}
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Created:</strong> {formatTime24(selectedCall.created_at)}
                {selectedCall.dispatched_at && <> | <strong>Dispatched:</strong> {formatTime24(selectedCall.dispatched_at)}</>}
                {selectedCall.closed_at && <> | <strong>Closed:</strong> {formatTime24(selectedCall.closed_at)}</>}
              </Typography>

              {/* Location */}
              {selectedCall.location_address && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Location:</strong> {selectedCall.location_address}
                  {selectedCall.location_apt ? ` Apt ${selectedCall.location_apt}` : ''}
                </Typography>
              )}
              {selectedCall.cross_street && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>X-Street:</strong> {selectedCall.cross_street}
                </Typography>
              )}

              {/* RP */}
              {selectedCall.reporting_party && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>RP:</strong> {selectedCall.reporting_party}
                  {selectedCall.reporting_phone ? ` — ${selectedCall.reporting_phone}` : ''}
                </Typography>
              )}

              {/* Agency */}
              {selectedCall.agency_name && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Agency:</strong> {selectedCall.agency_name}
                </Typography>
              )}

              {/* Units */}
              {selectedCall.units && selectedCall.units.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Units Assigned</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {selectedCall.units.map((u) => (
                      <Chip key={u.id} size="small" label={`${u.callsign} — ${u.status_name}`}
                        sx={{ bgcolor: u.status_color, color: '#fff' }} />
                    ))}
                  </Box>
                </>
              )}

              {/* Unit time punches (super-admin correctable) */}
              <CallTimesPanel
                callId={selectedCall.id}
                isSuperadmin={isSuperadmin}
                onChanged={() => { openDetail(selectedCall.id); doSearch(); }}
              />

              {/* Activity Log / Audit Trail */}
              {selectedCall.audit_trail && selectedCall.audit_trail.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Activity Log</Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 250, bgcolor: '#0d0d1a' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 0.5 }}>Time</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 0.5 }}>Action</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 0.5 }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 0.5 }}>Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCall.audit_trail.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell sx={{ fontSize: 12, py: 0.25, whiteSpace: 'nowrap' }}>
                              {formatTime24(a.timestamp)}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, py: 0.25 }}>
                              <Chip size="small" label={a.action.replace(/_/g, ' ')}
                                sx={{ bgcolor: ACTION_COLORS[a.action] || '#616161', color: '#fff', fontSize: 11, height: 20 }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, py: 0.25 }}>{a.username}</TableCell>
                            <TableCell sx={{ fontSize: 12, py: 0.25 }}>
                              {a.details}
                              {a.old_value && a.new_value ? ` (${a.old_value} → ${a.new_value})` : ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Narrative */}
              {selectedCall.narrative && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Narrative</Typography>
                  <Paper sx={{ p: 1, bgcolor: '#0d0d1a', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                      {selectedCall.narrative}
                    </Typography>
                  </Paper>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {selectedCall.status === 'closed' || selectedCall.status === 'cancelled' ? (
                <Button startIcon={<Replay />} color="warning" variant="contained" onClick={handleReopenCall}>Reopen Call</Button>
              ) : null}
              <Button startIcon={<PictureAsPdf />} onClick={() => { if (selectedCall) { const t = localStorage.getItem('token'); window.open(`/api/calls/${selectedCall.id}/pdf?token=${encodeURIComponent(t || '')}`, '_blank'); } }}>PDF Report</Button>
              <Button startIcon={<Email />} onClick={() => setEmailOpen(true)}>Email Report</Button>
              <Button onClick={() => setDetailOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Email Call Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Send the complete call report for <strong>{selectedCall?.cad_number}</strong> via email.
          </Typography>
          <TextField
            fullWidth label="Recipient Email" type="email" margin="normal"
            value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleEmail()}
            placeholder="recipient@example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEmail} disabled={!emailTo}>Send</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirm Dialog */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>Delete {selected.size} Call(s)?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            This permanently deletes the selected call(s) and their narratives/history. This cannot be undone.
          </Alert>
          <Typography variant="body2">
            Any units assigned to these calls are returned to Available. Remaining calls keep their CAD numbers;
            the next new call continues after the highest remaining number.
          </Typography>
          <Box sx={{ mt: 1, maxHeight: 160, overflow: 'auto' }}>
            {calls.filter((c) => selected.has(c.id)).map((c) => (
              <Chip key={c.id} size="small" label={c.cad_number} sx={{ mr: 0.5, mb: 0.5, fontFamily: 'monospace' }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete} disabled={deleting} startIcon={<DeleteForever />}>
            {deleting ? 'Deleting…' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
